import { EntityType } from '@frontend/common';
import { IdentificationClient } from '@frontend/identification/api';
import { Identification } from '@frontend/identification/types';
import { Logger } from '@frontend/logger';
import { UserClient } from '@frontend/user/api';
import { IUser } from '@frontend/user/types';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useScreen } from '../useScreen';

export interface ResetBadgeViewProps {
    error: string | null;
    resetError: () => void;
    linkedUser: IUser | null;
    badgeLinked: boolean;
    isResolvingLinkedUser: boolean;
    isResetting: boolean;
    onConfirmReset: () => void;
    onBack: () => void;
}

export const useResetBadge = (): ResetBadgeViewProps => {
    const intl = useIntl();
    const { onSubmit, onBack, wizardData } = useScreen();
    const badge = (wizardData.badge as Identification) ?? null;
    const wizardLinkedUser = (wizardData.linkedUser as IUser) ?? null;
    const badgeLinked = badge?.entity_type === EntityType.USER && !!badge.entity_id;
    const [linkedUser, setLinkedUser] = useState<IUser | null>(wizardLinkedUser?.id === badge?.entity_id ? wizardLinkedUser : null);
    const [isResolvingLinkedUser, setIsResolvingLinkedUser] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        if (!badgeLinked || !badge?.entity_id) {
            setLinkedUser(null);
            setIsResolvingLinkedUser(false);
            return;
        }

        if (wizardLinkedUser?.id === badge.entity_id) {
            setLinkedUser(wizardLinkedUser);
            setIsResolvingLinkedUser(false);
            return;
        }

        setIsResolvingLinkedUser(true);
        UserClient.resolveUsers([badge.entity_id])
            .then((users) => {
                if (!cancelled) setLinkedUser(users.results[0] ?? null);
            })
            .catch((e) => {
                Logger.error('Failed to resolve badge linked user', e);
                if (!cancelled) setLinkedUser(null);
            })
            .finally(() => {
                if (!cancelled) setIsResolvingLinkedUser(false);
            });

        return () => {
            cancelled = true;
        };
    }, [badge?.entity_id, badgeLinked, wizardLinkedUser]);

    const onConfirmReset = async () => {
        if (!badge || !badgeLinked || isResetting) return;
        setIsResetting(true);
        setError(null);

        try {
            await IdentificationClient.patchIdentification(badge.account_id, badge.id, {
                entity_id: null,
                entity_type: null,
                null_fields: ['entity_id', 'entity_type']
            });

            onSubmit();
        } catch (e) {
            Logger.error('Failed to reset badge', e);
            setError(intl.formatMessage({ id: 'kiosk.wizard.reset-badge.reset-failed', defaultMessage: 'Failed to reset badge. Please try again.' }));
        } finally {
            setIsResetting(false);
        }
    };

    return {
        error,
        resetError: () => setError(null),
        linkedUser,
        badgeLinked,
        isResolvingLinkedUser,
        isResetting,
        onConfirmReset,
        onBack
    };
};
